import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import MarketingLayout from '../components/marketing/MarketingLayout';
import { colors } from '../lib/colors';
import { getAllBlogPosts, BlogLoadError } from '../utils/enhancedBlogLoader';
import type { BlogPost } from '../utils/blogTypes';

export default function Blog() {
  // White paper-like background style
  const paperStyle = {
    background: '#ffffff',
    color: '#1a1a1a',
    borderRadius: '0.5rem',
    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)'
  };

  // State for blog posts and loading status
  const [allPosts, setAllPosts] = useState<BlogPost[]>([]);
  const [loadErrors, setLoadErrors] = useState<BlogLoadError[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTag, setSelectedTag] = useState<string | null>(null);
  
  // Load blog posts asynchronously
  useEffect(() => {
    async function loadBlogPosts() {
      try {
        setLoading(true);
        const { posts, errors } = await getAllBlogPosts();
        setAllPosts(posts);
        setLoadErrors(errors);
        
        // Log errors for debugging but don't block the UI
        if (errors.length > 0) {
          if (import.meta.env.DEV) {

            if (import.meta.env.DEV) {
              console.warn(`Failed to load ${errors.length} blog posts:`, errors);
            }

          }
        }
      } catch (err) {
        if (import.meta.env.DEV) {

          if (import.meta.env.DEV) {
            console.error('Error loading blog posts:', err);
          }

        }
        setError('Failed to load blog posts. Please try again later.');
      } finally {
        setLoading(false);
      }
    }
    
    loadBlogPosts();
  }, []);

  // Extract all unique tags
  const allTags: string[] = React.useMemo(() => {
    const tagSet = new Set<string>();
    allPosts.forEach(post => {
      (post.tags || []).forEach((tag: string) => tagSet.add(tag));
    });
    return Array.from(tagSet).sort();
  }, [allPosts]);

  // Tag filtering
  const filteredPosts: BlogPost[] = React.useMemo(() => {
    if (!selectedTag) return allPosts;
    return allPosts.filter(post => post.tags.includes(selectedTag));
  }, [allPosts, selectedTag]);

  // Group by category (Financial Goal/Life Stage)
  const grouped: { category: string; posts: BlogPost[] }[] = React.useMemo(() => {
    const groups: { [category: string]: BlogPost[] } = {};
    filteredPosts.forEach(post => {
      const cat = post.category || 'General';
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(post);
    });
    // Convert to array of {category, posts}
    return Object.entries(groups).map(([category, posts]) => ({ category, posts }));
  }, [filteredPosts]);

  // Loading state
  if (loading) {
    return (
      <MarketingLayout>
        <section className="min-h-screen bg-gray-100">
          <div className="max-w-4xl mx-auto py-12 px-3 text-center">
            <div style={paperStyle} className="p-8 rounded-lg">
              <h1 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900">Blog & Insights</h1>
              <div className="animate-pulse space-y-4">
                <div className="h-4 bg-gray-300 rounded w-3/4 mx-auto"></div>
                <div className="h-4 bg-gray-300 rounded w-1/2 mx-auto"></div>
                <div className="h-4 bg-gray-300 rounded w-2/3 mx-auto"></div>
              </div>
            </div>
          </div>
        </section>
      </MarketingLayout>
    );
  }
  
  // Error state
  if (error) {
    return (
      <MarketingLayout>
        <section className="min-h-screen bg-gray-100">
          <div className="max-w-4xl mx-auto py-12 px-3 text-center">
            <div style={paperStyle} className="p-8 rounded-lg">
              <h1 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900">Blog & Insights</h1>
              <p className="text-gray-700 mb-4">{error}</p>
              <button 
                className="bg-brand-primary hover:bg-brand-accent text-white font-bold py-2 px-6 rounded-lg transition-all"
                onClick={() => window.location.reload()}
              >
                Try Again
              </button>
            </div>
          </div>
        </section>
      </MarketingLayout>
    );
  }
  
  return (
    <MarketingLayout>
      <section className="min-h-screen bg-gray-100">
        <div className="max-w-4xl mx-auto py-12 px-3">
          <div style={paperStyle} className="p-8 rounded-lg">
          <h1 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900">Blog & Insights</h1>
          <p className="text-gray-700 max-w-xl mb-8 text-sm">
            Stay informed with expert articles on wealth planning, 
            tax strategies, and advanced insurance solutions for Saskatchewan and Alberta agricultural and business owners.
          </p>
          {/* Tag Filtering */}
          <div className="mb-8 flex flex-wrap gap-2">
            <button
              className={`px-3 py-1 rounded-full border text-xs ${!selectedTag ? 'bg-premium-dark text-white' : 'bg-gray-200 text-gray-700'}`}
              onClick={() => setSelectedTag(null)}
            >
              All Topics
            </button>
            {allTags.map((tag: string) => (
              <button
                key={tag}
                className={`px-3 py-1 rounded-full border text-xs ${selectedTag === tag ? 'bg-premium-dark text-white' : 'bg-gray-200 text-gray-700'}`}
                onClick={() => setSelectedTag(tag)}
              >
                {tag}
              </button>
            ))}
          </div>

          {/* Grouped by Category */}
          <div className="space-y-10">
            {allPosts.length === 0 ? (
              <div className="text-center py-12">
                <p className="text-gray-600">No blog posts found. Check the console for debugging information.</p>
              </div>
            ) : (
            grouped.map(({ category, posts }) => (
              <div key={category}>
                <h2 className="text-2xl font-bold mb-6 mt-10 text-premium-dark border-b-2 border-premium-light pb-3 inline-block bg-gradient-to-r from-premium-light/20 to-transparent px-4 py-2 rounded-t-lg shadow-sm">{category}</h2>
                <div className="space-y-6">
                  {posts.map(post => (
                    <div
                      key={post.slug}
                      className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm hover:shadow-md transition-shadow"
                    >
                      <h3 className="text-xl font-semibold mb-2 text-blue-800">{post.title}</h3>
                      <div className="flex flex-wrap gap-2 mb-2">
                        {post.tags.map(tag => (
                          <span key={tag} className="bg-blue-100 text-xs text-blue-800 px-2 py-0.5 rounded-full">{tag}</span>
                        ))}
                      </div>
                      <p className="text-gray-600 text-sm mb-4">{post.excerpt}</p>
                      <Link to={`/blog/${post.slug}`} className="bg-brand-primary hover:bg-brand-accent text-white font-bold py-2 px-5 rounded-lg text-sm inline-block transition-all">
                        Read More
                      </Link>
                    </div>
                  ))}
                </div>
              </div>
            ))
            )}
          </div>
          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}